
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { Box, Collapse, Divider, Drawer, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import { styled } from '@mui/material/styles';
import PatchTooltip from 'components/PatchTooltip';
import { FC, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { MenuItem, SubMenuItem } from 'types/navigation.type';
import { useUserGroups } from '../../contexts/UserGroupContext';
import { DRAWER_WIDTH } from 'components/constants';

const DrawerHeader = styled('div')({
  marginTop: '7%',
  fontWeight: '800',
  fontSize: '1.5rem',
  height: '80px',
});

const StyledListItem = styled(ListItem)({
  opacity: '0.95',
  color: 'white',
  fontWeight: '800',
  fontSize: '16px',
  marginLeft: '4%',
});

const StyledListItemButton = styled(ListItemButton)<{
  depth?: number;
  disabled?: boolean;
}>(({ depth = 0, disabled = false }) => ({
  color: 'white',
  textTransform: 'uppercase',
  paddingLeft: `${16 + depth * 16}px`,
  opacity: disabled ? 0.35 : 0.95,
  backgroundColor: '#131e31',
  cursor: disabled ? 'not-allowed' : 'pointer',
  '&:hover': {
    backgroundColor: '#0d1523',
    opacity: disabled ? 0.35 : 0.8,
  },
  '&:focus': {
    outline: 'none',
  },
  '&.active': {
    backgroundColor: '#ffe002',
    color: 'black',
    '&:hover': {
      backgroundColor: '#ffe002',
      opacity: 0.9,
    },
  },
  '&.Mui-disabled': {
    opacity: 0.35,
    color: 'white',
  },

  ...(depth > 0 && {
    backgroundColor: '#0d1523',
    textTransform: 'initial',
  }),
}));

const StyledListItemText = styled(ListItemText)({
  '& .MuiTypography-root': {
    fontFamily: ['Stratum1Bold', 'sans-serif'].join(','),
  },

  '& .MuiListItemText-primary': {
    color: 'inherit',
    fontSize: '16px',
  },
});

interface SideNavProps {
  items: MenuItem;
  mobileOpen: boolean;
  onDrawerClose: () => void;
  onDrawerTransitionEnd: () => void;
}

const isRouteActive = (url: string, currentPath: string): boolean => {
  return currentPath === url || currentPath.startsWith(url + '/');
};

const hasActiveChild = (item: SubMenuItem, currentPath: string): boolean => {
  if (isRouteActive(item.url, currentPath)) {
    return true;
  }
  if (item.items && item.items.length > 0) {
    return item.items.some(child => hasActiveChild(child, currentPath));
  }
  return false;
};

const NavListItem: FC<{ subOption: SubMenuItem; depth?: number }> = ({ subOption, depth = 0 }) => {
  const { isUserInGroup, hasAnyPermission } = useUserGroups();
  const location = useLocation();
  
  const isDisabled =
    subOption.enabled === false ||
    (subOption.userGroup && !isUserInGroup(subOption.userGroup)) ||
    (subOption.extendedPermission && !hasAnyPermission(subOption.extendedPermission));

  const hasChildren = subOption.items && subOption.items.length > 0;
  const hasActiveDescendant = hasChildren && hasActiveChild(subOption, location.pathname);

  const [isExpanded, setIsExpanded] = useState(() => hasActiveDescendant);

  const handleClick = () => {
    if (hasChildren) {
      setIsExpanded(prev => !prev);
    }
  };

  return (
    <>
      <PatchTooltip title={subOption.hovertext}>
        <StyledListItemButton
          {...(!hasChildren && { component: NavLink, to: isDisabled ? '/disabled' : subOption.url })}
          onClick={handleClick}
          disabled={!!isDisabled}
          depth={depth}
        >
          <StyledListItemText primary={subOption.name} />
          {hasChildren && (isExpanded ? <ExpandLess /> : <ExpandMore />)}
        </StyledListItemButton>
      </PatchTooltip>

      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {subOption.items?.map(childItem => (
              <NavListItem key={childItem.name} subOption={childItem} depth={depth + 1} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
};

const SideNav: FC<SideNavProps> = ({ items, mobileOpen, onDrawerClose, onDrawerTransitionEnd }) => {
  const drawerContent = (
    <>
      <DrawerHeader>
        <StyledListItem>
          <ListItemText>
            <h1>{items.name}</h1>
          </ListItemText>
        </StyledListItem>
      </DrawerHeader>

      <Divider />
      <List component="nav">
        {items.items.map(item => (
          <NavListItem key={item.name} subOption={item} />
        ))}
      </List>
    </>
  );

  return (
    <Box component="nav" sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }} aria-label="navigation">
      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onTransitionEnd={onDrawerTransitionEnd}
        onClose={onDrawerClose}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: DRAWER_WIDTH,
            backgroundColor: '#121e31',
          },
        }}
        slotProps={{
          root: {
            keepMounted: true,
          },
        }}
      >
        {drawerContent}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: DRAWER_WIDTH,
            backgroundColor: '#121e31',
            position: 'fixed',
            height: '100%',
            paddingBottom: '80px',
          },
        }}
        open
      >
        <List component="nav">
          {items.items.map(item => (
            <NavListItem key={item.name} subOption={item} />
          ))}
        </List>
      </Drawer>
    </Box>
  );
};

export default SideNav;